"use client";

import { cn } from "@universe/ui/lib/utils";
import Image from "next/image";
import { useState } from "react";

import { useWhitelabel } from "@/providers";

import UPLogo from "./up-logo";

type LogoVariant = "default" | "light";
type LogoSize = "sm" | "md" | "lg";

export interface CompanyLogoProps {
  variant?: LogoVariant;
  size?: LogoSize;
  width?: number;
  height?: number;
  className?: string;
  id?: string;
  onClick?: () => void;
  /** Override the default UPLogo image src (non-whitelabel only) */
  overrideSrc?: string;
}

const sizeMap: Record<LogoSize, { width: number; height: number }> = {
  sm: { width: 80, height: 80 },
  md: { width: 140, height: 140 },
  lg: { width: 220, height: 220 },
};

// Text fallback component when no logo is available
function TextFallback({
  companyName,
  onClick,
  className,
}: {
  companyName?: string;
  onClick?: () => void;
  className?: string;
}) {
  if (!companyName) {
    return null;
  }

  return (
    <div
      onClick={onClick}
      className={cn(
        "flex items-center justify-center py-4",
        onClick && "cursor-pointer hover:opacity-80",
        className
      )}
      style={{
        paddingTop: "var(--logo-padding-y, var(--logo-padding, 0))",
        paddingBottom: "var(--logo-padding-y, var(--logo-padding, 0))",
        paddingLeft: "var(--logo-padding-x, var(--logo-padding, 0))",
        paddingRight: "var(--logo-padding-x, var(--logo-padding, 0))",
      }}
    >
      <span className="text-2xl font-bold text-white">{companyName}</span>
    </div>
  );
}

/**
 * Try the alternate image format (.png <-> .svg) for a given URL.
 * Returns undefined if the URL doesn't end with a known image extension.
 */
function getAlternateFormatUrl(url: string): string | undefined {
  if (url.endsWith(".png")) return url.replace(/\.png$/, ".svg");
  if (url.endsWith(".svg")) return url.replace(/\.svg$/, ".png");
  return undefined;
}

export const CompanyLogo = ({
  variant = "light",
  size = "md",
  width,
  height,
  className = "",
  id,
  onClick,
  overrideSrc,
}: CompanyLogoProps) => {
  const whitelabel = useWhitelabel();
  const [imageError, setImageError] = useState(false);
  const [triedAlternate, setTriedAlternate] = useState(false);

  const dimensions = sizeMap[size];
  const finalWidth = width ?? dimensions.width;
  const finalHeight = height ?? dimensions.height;

  // If not a whitelabel, fall back to UPLogo
  if (!whitelabel.isWhitelabel) {
    return (
      <UPLogo
        id={id}
        width={finalWidth}
        height={finalHeight}
        className={className}
        variant={variant === "default" ? "blue" : "white"}
        overrideSrc={overrideSrc}
        onClick={onClick}
      />
    );
  }

  // Use the whitelabel logo based on variant
  const getLogoSrc = () => {
    switch (variant) {
      case "light":
        return whitelabel.lightLogo ?? whitelabel.defaultLogo;
      case "default":
      default:
        return whitelabel.defaultLogo;
    }
  };
  const resolvedLogoSrc = getLogoSrc();

  // If primary failed, try alternate format (.png <-> .svg) before text fallback
  const logoSrc =
    imageError && resolvedLogoSrc
      ? getAlternateFormatUrl(resolvedLogoSrc)
      : resolvedLogoSrc;

  // No logo URL or both formats failed - show text fallback
  if (!logoSrc || (imageError && triedAlternate)) {
    return (
      <TextFallback
        companyName={whitelabel.companyName}
        onClick={onClick}
        className={className}
      />
    );
  }

  const baseStyle: React.CSSProperties = {
    paddingTop: "var(--logo-padding-y, var(--logo-padding, 0))",
    paddingBottom: "var(--logo-padding-y, var(--logo-padding, 0))",
    paddingLeft: "var(--logo-padding-x, var(--logo-padding, 0))",
    paddingRight: "var(--logo-padding-x, var(--logo-padding, 0))",
    filter: "var(--logo-filter, none)",
    width: "var(--logo-width, fit-content)",
    height: "var(--logo-height, fit-content)",
  };

  return (
    <div
      onClick={onClick}
      className={cn(
        "py-4 overflow-visible",
        onClick && "cursor-pointer hover:opacity-80",
        className
      )}
      style={baseStyle}
    >
      <Image
        id={id}
        src={logoSrc}
        alt={
          whitelabel.companyName
            ? `${whitelabel.companyName} Logo`
            : "Company Logo"
        }
        width={finalWidth}
        height={finalHeight}
        className={cn(className, "w-full h-full object-contain")}
        priority
        unoptimized
        onError={() => {
          if (imageError) {
            // Already tried alternate format, give up
            setTriedAlternate(true);
          } else {
            setImageError(true);
          }
        }}
      />
    </div>
  );
};

export default CompanyLogo;
